<template>
  <div id="product" itemscope itemtype="http://schema.org/Product">
    <SfBreadcrumbs
      class="breadcrumbs desktop-only container-xl"
      :breadcrumbs="breadcrumbs"
    >
      <template #link="{ breadcrumb }">
        <router-link
          :to="breadcrumb.route.link"
          class="sf-breadcrumbs__breadcrumb"
        >
          {{ breadcrumb.text }}
        </router-link>
      </template>
    </SfBreadcrumbs>

    <OProductDetails
      :product="getCurrentProduct"
      :product-gallery="getProductGallery"
      :product-configuration="getCurrentProductConfiguration"
      :product-custom-options="getCurrentCustomOptions"
      :product-attributes="getCustomAttributes"
      :product-stock="stock"
    />

    <hr class="divider container-xl" />

    <div class="product__bottom">
      <lazy-hydrate when-idle>
        <SfSection :title-heading="$t('Similar Products')" class="container-xl">
          <MRelatedProducts type="related" />
        </SfSection>
      </lazy-hydrate>

      <lazy-hydrate when-idle>
        <SfSection
          :title-heading="$t('We found other products you might like')"
          class="container-xl"
        >
          <MRelatedProducts type="upsell" />
        </SfSection>
      </lazy-hydrate>

      <!--
      <lazy-hydrate when-idle>
        <SfSection v-show="banners.length">
          <router-link :key="i" :to="banner.link" v-for="(banner, i) in banners">
            <SfBanner
              :subtitle="banner.subtitle"
              :title="banner.title"
              :image="banner.image"
              class="banner sf-banner--slim"
            />
          </router-link>
        </SfSection>
      </lazy-hydrate>
      -->
    </div>

    <MSectionUsp />

    <MSectionNewsletter />
  </div>
</template>

<script>
import { mapGetters, mapState } from "vuex";
import LazyHydrate from "vue-lazy-hydration";
import { currentStoreView } from "@vue-storefront/core/lib/multistore";
import { htmlDecode } from "@vue-storefront/core/filters";
import { ReviewModule } from "@vue-storefront/core/modules/review";
import { registerModule } from "@vue-storefront/core/lib/modules";
import { onlineHelper, isServer } from "@vue-storefront/core/helpers";
import { catalogHooksExecutors } from "@vue-storefront/core/modules/catalog-next/hooks";
import MRelatedProducts from "theme/components/molecules/m-related-products";
import OProductDetails from "theme/components/organisms/o-product-details";
// import AImagesGrid from 'theme/components/atoms/a-images-grid';
import { checkWebpSupport } from "theme/helpers";
import { SfSection, SfBreadcrumbs } from "@storefront-ui/vue";
import { filterChangedProduct } from "@vue-storefront/core/modules/catalog/events";
import MSectionUsp from "theme/components/molecules/m-section-usp";
import MSectionNewsletter from "theme/components/molecules/m-section-newsletter";
import {
  loadStock,
  resolveStock,
} from "src/modules/vsf-inventory-sources/helpers/stockResolver";

export default {
  name: "Product",
  components: {
    LazyHydrate,
    MRelatedProducts,
    SfSection,
    OProductDetails,
    // SfBanner,
    // AImagesGrid,
    SfBreadcrumbs,
    MSectionUsp,
    MSectionNewsletter,
  },
  provide() {
    return {
      configurableOptionCallback: this.configurableOptionCallback,
    };
  },
  data() {
    return {
      stock: {
        isLoading: false,
        max: 0,
        maxPickup: 0,
        manageQuantity: true,
      },
    };
  },
  computed: {
    ...mapState({
      isWebpSupported: (state) => state.ui.isWebpSupported,
    }),
    ...mapGetters({
      getCurrentCategory: "category-next/getCurrentCategory",
      getCurrentProduct: "product/getCurrentProduct",
      getProductGallery: "product/getProductGallery",
      getCurrentProductConfiguration: "product/getCurrentProductConfiguration",
      getOriginalProduct: "product/getOriginalProduct",
      attributesByCode: "attribute/attributeListByCode",
      getCurrentCustomOptions: "product/getCurrentCustomOptions",
      promotedOffers: "promoted/getPromotedOffers",
      dummyInstagramImages: "instagram/getInstagramImages",
      getBreadcrumbsRoutes: "breadcrumbs/getBreadcrumbsRoutes",
      getBreadcrumbsCurrent: "breadcrumbs/getBreadcrumbsCurrent",
      getCurrentStore: "inventorySources/getCurrent",
    }),
    breadcrumbs() {
      return this.getBreadcrumbsRoutes
        .map((route) => ({
          text: htmlDecode(route.name),
          route: {
            link: route.route_link,
          },
        }))
        .concat({
          text: htmlDecode(this.getBreadcrumbsCurrent),
        });
    },
    isOnline() {
      return onlineHelper.isOnline;
    },
    getCustomAttributes() {
      return Object.values(this.attributesByCode)
        .filter((a) => {
          return (
            a.is_visible &&
            a.is_user_defined &&
            (parseInt(a.is_visible_on_front) ||
              a.is_visible_on_front === true) &&
            this.getCurrentProduct[a.attribute_code]
          );
        })
        .sort((a, b) => {
          return a.position - b.position;
        });
    },
    banners() {
      return checkWebpSupport(
        this.promotedOffers.productBanners,
        this.isWebpSupported
      );
    },
    instagramImages() {
      return checkWebpSupport(this.dummyInstagramImages, this.isWebpSupported);
    },
  },
  watch: {
    isOnline: {
      handler(isOnline) {
        if (isOnline) {
          this.getQuantity();
        }
      },
    },
    getCurrentStore: {
      handler(store) {
        if (store) {
          this.getQuantity();
        }
      },
    },
  },
  async asyncData({ store, route, context }) {
    if (context) context.output.cacheTags.add("product");
    const product = await store.dispatch("product/loadProduct", {
      parentSku: route.params.parentSku,
      childSku:
        route && route.params && route.params.childSku
          ? route.params.childSku
          : null,
    });
    const loadBreadcrumbsPromise = store.dispatch(
      "product/loadProductBreadcrumbs",
      { product }
    );
    if (isServer) await loadBreadcrumbsPromise;
    catalogHooksExecutors.productPageVisited(product);
  },
  beforeCreate() {
    registerModule(ReviewModule);
  },
  async mounted() {
    await Promise.all([
      this.$store.dispatch("review/list", {
        productId: this.getOriginalProduct.id,
      }),
      this.$store.dispatch("instagram/updateInstagramImages"),
    ]);

    this.$bus.$on("product-after-bundleoptions", () => {
      this.getQuantity();
    });
  },
  beforeRouteEnter(to, from, next) {
    if (isServer) {
      next();
    } else {
      next((vm) => {
        vm.getQuantity();
      });
    }
  },
  methods: {
    async configurableOptionCallback(variant) {
      const selectedConfiguration = Object.assign(
        { attribute_code: variant.type },
        variant
      );
      await filterChangedProduct(
        selectedConfiguration,
        this.$store,
        this.$router
      );
      this.getQuantity();
    },
    async getQuantity() {
      if (this.stock.isLoading || !this.getCurrentProduct) return; // stock info is already loading
      this.stock.isLoading = true;
      try {
        await loadStock(this.getCurrentProduct);
        let stock = resolveStock(this.getCurrentProduct);
        this.stock.max = stock.qty ? stock.qty : 0;
        this.stock.maxPickup = stock.qtyPickup ? stock.qtyPickup : 0;
        this.stock.thresholdQuantity = parseInt(
          this.$store.getters["storeConfig/getConfigValue"](
            "cataloginventory/options/stock_threshold_qty"
          )
        );
        this.manageQuantity = true;
      } finally {
        this.stock.isLoading = false;
      }
    },
  },
  metaInfo() {
    const storeView = currentStoreView();
    return {
      title: htmlDecode(
        this.getCurrentProduct.meta_title || this.getCurrentProduct.name
      ),
      meta: this.getCurrentProduct.meta_description
        ? [
            {
              vmid: "description",
              name: "description",
              content: htmlDecode(this.getCurrentProduct.meta_description),
            },
          ]
        : [],
    };
  },
};
</script>

<style lang="scss" scoped>
@import "~@storefront-ui/shared/styles/helpers/breakpoints";

#product {
  box-sizing: border-box;
  @include for-desktop {
    margin: 0 auto;
  }
}

.product__bottom {
  box-sizing: border-box;
  padding: 0 var(--spacer-sm);
  @include for-desktop {
    padding: 0;
  }
}

::v-deep {
  .product__colors button {
    border: 1px solid var(--c-light);
  }
}

.banner {
  margin: var(--spacer-xl) 0;
  @include for-desktop {
    margin: var(--spacer-2xl) 0;
  }
}
</style>
